import { useState, useCallback } from 'react';
import type { FetchState } from '../../types/common/fetchState.types';
import {
  isFetchSuccess,
  isFetchError,
} from '../../types/common/fetchState.types';
import { API_ERROR_MESSAGES } from '../../constants/messages.constant';
import { API_CONFIG } from '../../constants/api.constants';
import { fetchJson } from '../utils/fetchJson';

export interface UseFetchOptions<T> {
  /**
   * URL to fetch from
   */
  url: string;

  /**
   * Custom fetch options (headers, method, body, etc.)
   */
  fetchOptions?: RequestInit;

  /**
   * Request timeout in milliseconds
   * Request will be aborted if it takes longer than this
   * @default API_CONFIG.DEFAULT_TIMEOUT (10000ms)
   */
  timeout?: number;

  /**
   * Optional transform/validation function
   * Allows custom validation or transformation of response data
   */
  transform?: (data: unknown) => T;
}

export interface UseFetchResult<T> {
  /**
   * Current fetch state (discriminated union)
   * Use the status field to determine which properties are available
   */
  state: FetchState<T>;

  /**
   * Function to trigger fetch manually
   * Returns a promise that resolves when fetch completes
   */
  fetchData: () => Promise<void>;

  /**
   * Convenience boolean flags derived from state
   */
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}

/**
 * Generic fetch hook with AbortController and proper state management
 *
 * Features:
 * - Discriminated union state for type safety
 * - AbortController to cancel requests on unmount
 * - Timeout protection to prevent hanging requests
 * - Optional transform function for custom validation
 * - User-friendly error messages (no sensitive data exposed)
 *
 * @example
 * ```typescript
 * const { state, fetchData, isLoading } = useFetch<PublicScheduleAPIResponse>({
 *   url: '/schedule/mls-next/2026.json'
 * });
 *
 * // Trigger fetch manually
 * useEffect(() => {
 *   fetchData();
 * }, [fetchData]);
 *
 * // Access data safely with type guards
 * if (isFetchSuccess(state)) {
 *   console.log(state.data.events); // TypeScript knows data is available
 * }
 * ```
 */
export const useFetch = <T>({
  url,
  fetchOptions,
  timeout = API_CONFIG.DEFAULT_TIMEOUT,
  transform,
}: UseFetchOptions<T>): UseFetchResult<T> => {
  const [state, setState] = useState<FetchState<T>>({
    status: 'idle',
    data: null,
    error: null,
  });

  const fetchData = useCallback(async () => {
    // Preserve previous successful data while loading, so a background
    // refresh doesn't flicker the UI back to empty.
    setState((prevState) => ({
      status: 'loading',
      data: isFetchSuccess(prevState) ? prevState.data : null,
      error: null,
    }));

    try {
      const jsonData = await fetchJson<unknown>(url, {
        ...fetchOptions,
        timeout,
      });
      const transformedData = transform ? transform(jsonData) : (jsonData as T);

      setState({
        status: 'success',
        data: transformedData,
        error: null,
      });
    } catch (error) {
      // Ignore abort errors (normal cleanup, not a real error)
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      const errorMessage =
        error instanceof Error
          ? error.message
          : API_ERROR_MESSAGES.NETWORK_ERROR;

      setState({
        status: 'error',
        data: null,
        error: new Error(errorMessage),
      });
    }
  }, [url, fetchOptions, timeout, transform]);

  return {
    state,
    fetchData,
    isLoading: state.status === 'loading',
    isError: isFetchError(state),
    isSuccess: isFetchSuccess(state),
  };
};
